import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/useAuth';
import { AuthApiError, requestBetaAccess } from '../../services/authApi';

type RequestState = 'idle' | 'submitting' | 'submitted' | 'error';

export function HeroSection() {
  const { isAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [requestState, setRequestState] = useState<RequestState>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  async function handleRequestAccess(e: FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;

    setRequestState('submitting');
    setErrorMessage('');

    try {
      await requestBetaAccess(email.trim());
      setRequestState('submitted');
    } catch (err) {
      console.error('Beta access request failed:', err);
      if (err instanceof AuthApiError && err.status === 429) {
        setErrorMessage('Too many requests. Please wait a few minutes and try again.');
      } else {
        setErrorMessage('Something went wrong. Please try again.');
      }
      setRequestState('error');
    }
  }

  return (
    <section className="landing-hero" id="beta-request">
      <div className="landing-hero-content">
        <h2 className="landing-hero-title">
          See every year of your retirement — before you get there
        </h2>
        <p className="landing-hero-subtitle">
          Glide-Path models your pensions, ISAs, and property year by year, with UK tax rules built in.
          See what you can draw, when to draw it, and whether your money lasts.
        </p>

        {isAuthenticated ? (
          <div className="landing-hero-ctas">
            <Link to="/plan" className="landing-btn landing-btn-primary">Go to My Plan</Link>
            <Link to="/tools" className="landing-btn landing-btn-ghost">Explore Tools</Link>
          </div>
        ) : import.meta.env.DEV ? (
          <>
            <p className="dev-mode-notice-landing">Dev mode — beta gate bypassed</p>
            <div className="landing-hero-ctas">
              <Link to="/register" className="landing-btn landing-btn-primary">Register</Link>
              <Link to="/login" className="landing-btn landing-btn-ghost">Sign in</Link>
            </div>
          </>
        ) : (
          <div className="beta-request-section">
            {requestState === 'submitted' ? (
              <div className="beta-request-confirmation">
                <p className="beta-request-confirmation-text">
                  We've got your request. We review manually and aim to respond within 24–48 hours.
                </p>
                <a href="#features" className="beta-request-explore-link">
                  Explore what's included &darr;
                </a>
              </div>
            ) : (
              <>
                <form className="beta-request-form" onSubmit={handleRequestAccess}>
                  <div className="beta-request-form-row">
                    <input
                      type="email"
                      className="beta-request-input"
                      placeholder="you@example.com"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      disabled={requestState === 'submitting'}
                      required
                      aria-label="Email address"
                    />
                    <button
                      type="submit"
                      className="landing-btn landing-btn-primary beta-request-btn"
                      disabled={requestState === 'submitting' || !email.trim()}
                    >
                      {requestState === 'submitting' ? 'Sending...' : 'Request Access'}
                    </button>
                  </div>
                  {requestState === 'error' && (
                    <p className="beta-request-error" role="alert">{errorMessage}</p>
                  )}
                  <p className="beta-request-privacy">
                    We only use this to send your invitation.{' '}
                    <Link to="/privacy">Privacy Policy</Link>
                  </p>
                </form>
                <p className="beta-request-disclaimer">
                  An educational planning tool — not financial advice. All projections are illustrative.
                </p>
                <p className="beta-request-signin">
                  Already have an account? <Link to="/login">Sign in</Link>
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
